import { useCallback } from 'react';

import type { UseFormReturn } from 'react-hook-form';

import type {
  NameConverter,
  OnRevert,
  PivotClassifier,
  PivotValueCalculator,
  QueryToFormConverter,
  ValueConverter,
} from '@/_containers';
import { snackbar } from '@/_shared';

import { invalidateAgreementQuery } from '../apis';
import { type DeliverablesFormSchema, type DeliverablesQuerySchema } from '../deliverables';
import { useDeliverableBulkUpdateMutation } from '../deliverables/api';
import { deliverableFormToQuery, deliverableQueryToForm } from '../deliverables/helper';
import type { AgreementFormSchema } from '../schemas';

export function useDeliverableRevisionHistory(
  form: UseFormReturn<AgreementFormSchema>,
  handleDeliverableDelete: (id: number) => Promise<void>,
  handleDeliverableCreate: (
    dto: DeliverablesFormSchema,
    lockdownOverridePin?: string,
    overrideJustificationNote?: string
  ) => Promise<{ data: DeliverablesQuerySchema }>
) {
  const { mutateAsync: deliverablesMutateUpdateAsync } = useDeliverableBulkUpdateMutation();

  const validatePivot = useCallback((_: keyof DeliverablesFormSchema) => {
    return true;
  }, []);

  const onRevert: OnRevert<DeliverablesFormSchema> = useCallback(
    async (oldValue, field, type) => {
      console.log({ oldValue, field, type });

      if (type === 'ADD') {
        await handleDeliverableDelete(oldValue.id as number);
        snackbar.success('Deliverable has been deleted.');
      } else if (type === 'DELETE') {
        await handleDeliverableCreate({
          ...oldValue,
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          price_frequency: { id: oldValue.price_frequency_id, name: oldValue.price_frequency_id },
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          product: { id: oldValue.product_id, sku: oldValue.sku, name: oldValue.product_name },
        } as DeliverablesFormSchema);
        await invalidateAgreementQuery(`${oldValue.agreement_id}`);
        snackbar.success('Deliverable has been added.');
      } else if (type === 'UPDATE') {
        const { tempAgreement_deliverables, agreement_id } = form.getValues();
        const deliverable = tempAgreement_deliverables.find((d) => d.id === oldValue.id);
        if (!deliverable) {
          console.log("COULDN'T FOUND");
          snackbar.error("Deliverable not found. Couldn't revert the changes.");
        }
        const newDeliverables = tempAgreement_deliverables.map((d) => {
          if (d.id === oldValue.id) {
            return { ...d, ...oldValue };
          }
          return d;
        });

        const nDeliverables = deliverableFormToQuery(newDeliverables);
        const res = await deliverablesMutateUpdateAsync({ items: nDeliverables });
        if (res) {
          form.setValue('agreement_deliverables', newDeliverables);
          await invalidateAgreementQuery(`${agreement_id}`);
          snackbar.success('Deliverable has been updated.');
        }
      }
    },
    [deliverablesMutateUpdateAsync, form, handleDeliverableCreate, handleDeliverableDelete]
  );

  const queryToFormConverter: QueryToFormConverter<DeliverablesQuerySchema, DeliverablesFormSchema> = useCallback(
    (q) => {
      return deliverableQueryToForm([{ ...q }])[0];
    },
    []
  );

  const nameConverter: NameConverter<DeliverablesFormSchema> = useCallback(() => {
    return 'Deliverables';
  }, []);

  const valueConverter: ValueConverter<DeliverablesFormSchema> = useCallback(() => {
    return null;
  }, []);

  const pivotClassifier: PivotClassifier<DeliverablesFormSchema> = useCallback(() => {
    return { isPivot: true, changeSetExtractorType: 'DELIVERABLE', isUseable: true };
  }, []);

  const pivotValueCalculator: PivotValueCalculator<DeliverablesFormSchema> = useCallback(() => {
    return [];
  }, []);

  return {
    queryToFormConverter,
    nameConverter,
    valueConverter,
    onRevert,
    validatePivot,
    pivotClassifier,
    pivotValueCalculator,
  };
}
